import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { TeamsAutoAttendantsCallFlowEditComponent } from './auto-attendants-call-flow-edit/teams-auto-attendants-call-flow-edit.component';
import { HolidayCallFlowFormComponent } from './holidays/holiday-call-flow-form.component';
import { WeeklyScheduleComponent } from './weekly-schedule/weekly-schedule.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TrimWithEllipsisFilter } from '../../../shared/filters/trim-with-ellipsis.filter';
import {
  CallFlowGreetingsEntity,
  CallFlowGreetingsFormComponent,
  CallFlowGreetingsOptions,
} from './call-flow-greetings/call-flow-greetings-form.component';
import { TeamsAutoAttendantEditContext } from '../../teams-auto-attendant-edit.context';
import { cloneDeep } from 'lodash';
import { Observable, of } from 'rxjs';
import { ButtonSizes, ButtonStyles, ZiroButtonComponent } from '../../../button/button.component';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import {
  AutoAttendantHoliday,
  CallFlow,
  CallRoutingOption,
  VoiceAppViewEntity,
  WeeklySchedule,
} from '../../../shared/models/generated/ziroModels';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ZiroFormsModule } from '../../../forms/ziro-forms.module';

export interface CallFlowGreetingUpdate {
  flowId: string;
  greeting: CallFlowGreetingsEntity;
}

export interface UiCallFlow extends CallFlow {
  id: string;
  isHoliday?: boolean;
  isAfterHoursCallFlow?: boolean;
  holiday?: AutoAttendantHoliday;
  schedule?: WeeklySchedule;
  selectedGreetingOption?: CallFlowGreetingsOptions;
  selectedDestinationGreetingOption?: CallFlowGreetingsOptions;
  greetingFile?: File;
  redirectCallSettingsEntity?: VoiceAppViewEntity;
}

@Component({
  selector: 'ziro-teams-auto-attendants-call-flow',
  templateUrl: './teams-auto-attendants-call-flow.component.html',
  styleUrl: './teams-auto-attendants-call-flow.component.scss',
  imports: [
    TeamsAutoAttendantsCallFlowEditComponent,
    HolidayCallFlowFormComponent,
    CallFlowGreetingsFormComponent,
    WeeklyScheduleComponent,
    TranslateModule,
    NgxSkeletonLoaderModule,
    ZiroFormsModule,
    NgbTooltip,
    ZiroButtonComponent,
  ],
})
export class TeamsAutoAttendantsCallFlowComponent implements OnInit {
  @ViewChildren(TeamsAutoAttendantsCallFlowEditComponent)
  callFlowEditComponents!: QueryList<TeamsAutoAttendantsCallFlowEditComponent>;

  @ViewChild(HolidayCallFlowFormComponent) holidayForm?: HolidayCallFlowFormComponent;
  @ViewChild(CallFlowGreetingsFormComponent) greetingsForm?: CallFlowGreetingsFormComponent;

  private _editContext = inject(TeamsAutoAttendantEditContext);
  private _translateService = inject(TranslateService);
  private _ziroModalService = inject(ZiroModalService);
  private _trimWithEllipsisFilter = inject(TrimWithEllipsisFilter);

  callFlowNameLimit = 40;

  // inputs
  standardCallFlows = input.required<UiCallFlow[]>();
  holidayCallFlows = input<UiCallFlow[]>();
  selectedCallFlow = signal<UiCallFlow | null>(null);
  tenantId = input.required<string>();
  allHolidays = input.required<AutoAttendantHoliday[]>();
  isDisabled = input<boolean>();

  // outputs
  greetingUpdate = output<CallFlowGreetingUpdate>();

  flowsWithErrors = signal<Set<string>>(new Set());
  private _validatedFlowIds = new Set<string>();
  private _editComponentValid = new Map<string, boolean>();
  private _holidayValid = true;
  private _greetingsValidByFlowId = new Map<string, boolean>();

  currentGreeting = computed(() => {
    const flow = this.selectedCallFlow();
    if (!flow) {
      return undefined;
    }
    const state = this._editContext.getFlowState(flow.id);
    return state?.greetingOption ?? flow.greetingOption;
  });

  currentPendingGreetingOption = computed(() => {
    const flow = this.selectedCallFlow();
    if (!flow) {
      return undefined;
    }
    const state = this._editContext.getFlowState(flow.id);
    return state?.pendingGreetingOption ?? flow.selectedGreetingOption;
  });

  currentGreetingFile = computed(() => {
    const flow = this.selectedCallFlow();
    if (!flow) {
      return undefined;
    }
    const state = this._editContext.getFlowState(flow.id);
    return state?.pendingGreetingFile ?? flow.greetingFile;
  });

  currentSchedule = computed(() => {
    const flow = this.selectedCallFlow();
    if (!flow) {
      return undefined;
    }
    const state = this._editContext.getFlowState(flow.id);
    return state?.schedule ?? flow.schedule;
  });

  onScheduleChange(schedule: WeeklySchedule): void {
    const flowId = this.selectedCallFlow()?.id;
    if (flowId) {
      this._editContext.updateSchedule(flowId, schedule);
    }
  }

  get localHolidayCallFlows(): WritableSignal<UiCallFlow[]> {
    return this._editContext.localHolidayCallFlows;
  }

  readonly allCallFlows = computed(() => [...this.standardCallFlows(), ...this.localHolidayCallFlows()]);

  constructor() {
    effect(() => {
      this._editContext.localHolidayCallFlows();
      queueMicrotask(() => this._revalidateSelectedHolidayForm());
    });
  }

  ngOnInit(): void {
    const firstFlow = this.standardCallFlows()[0] ?? this._editContext.localHolidayCallFlows()[0] ?? null;
    this.selectedCallFlow.set(firstFlow);
  }

  onCallFlowSelected(flow: UiCallFlow): void {
    this._saveAndValidateCurrentFlow();
    this.selectedCallFlow.set(flow);
  }

  isExpanded(flowId: string): boolean {
    return this.selectedCallFlow()?.id === flowId;
  }

  toggleFlow(flow: UiCallFlow): void {
    if (this.selectedCallFlow()?.id === flow.id) {
      this._saveAndValidateCurrentFlow();
      this.selectedCallFlow.set(null);
      return;
    }
    this.onCallFlowSelected(flow);
  }

  tooltipOnTruncate(label: string): string {
    if (!label) {
      return '';
    }
    const trimmed = this._trimWithEllipsisFilter.transform(label, this.callFlowNameLimit);
    return trimmed.endsWith('...') ? label : '';
  }

  onHolidayValidityChange(isValid: boolean): void {
    this._holidayValid = isValid;
    const flowId = this.selectedCallFlow()?.id;
    if (flowId) {
      this._refreshCombinedValidity(flowId);
    }
  }

  onGreetingsValidityChange(isValid: boolean): void {
    const flowId = this.selectedCallFlow()?.id;
    if (flowId) {
      this._greetingsValidByFlowId.set(flowId, isValid);
      this._refreshCombinedValidity(flowId);
    }
  }

  private _saveAndValidateCurrentFlow(): void {
    const current = this.selectedCallFlow();
    if (!current) {
      return;
    }
    const greetingEntity = this.greetingsForm?.entity;
    if (greetingEntity) {
      this._editContext.updateGreeting(
        current.id,
        greetingEntity.greeting ?? null,
        greetingEntity.selectedGreetingOption,
        false,
        greetingEntity.greetingFile,
      );
    }
    this._validatedFlowIds.add(current.id);
    this.greetingsForm?.triggerSubmitValidation();
    this._greetingsValidByFlowId.set(current.id, this.greetingsForm?.isFormValid() ?? true);
    if (current.isHoliday) {
      this.holidayForm?.triggerSubmitValidation();
      this._holidayValid = this.holidayForm?.isFormValid() ?? true;
    }
    const currentEditComponent = this.callFlowEditComponents.find((c) => c.callFlow().id === current.id);
    currentEditComponent?.validateAndShowErrors();
    this._editComponentValid.set(current.id, currentEditComponent?.isValid() ?? true);
    this._refreshCombinedValidity(current.id);
  }

  private _refreshCombinedValidity(flowId: string): void {
    if (!this._validatedFlowIds.has(flowId)) {
      return;
    }
    const editValid = this._editComponentValid.get(flowId) ?? true;
    const isSelected = this.selectedCallFlow()?.id === flowId;
    const flow = this.allCallFlows().find((candidate) => candidate.id === flowId);
    const greetingsValid = flow ? this._resolveGreetingsValid(flow) : true;
    const holidayValid = isSelected && this.selectedCallFlow()?.isHoliday ? this._holidayValid : true;
    this._refreshFlowError(flowId, editValid && greetingsValid && holidayValid);
  }

  onDuplicateHolidayCallFlow(flow: UiCallFlow): void {
    const callFlowCopy = cloneDeep(flow);
    const newCallFlow: UiCallFlow = {
      ...callFlowCopy,
      id: crypto.randomUUID(),
      name: `${callFlowCopy.name} (copy)`,
      isHoliday: true,
      isAfterHoursCallFlow: false,
      holiday: {
        id: null,
        name: '',
        dateTimeRanges: [],
      },
    };
    delete newCallFlow.schedule;
    this._editContext.addHolidayCallFlow(newCallFlow);
    this.selectedCallFlow.set(newCallFlow);
    this._validatedFlowIds.add(newCallFlow.id);
    setTimeout(() => this._forceValidateAllFlows());
  }

  onDeleteHolidayCallFlow(flow: UiCallFlow): void {
    const options = {
      modalViewProperties: {
        promptBody: this._translateService.instant(
          'tkey;admin.teams_voice_management.teams_auto_attendant.delete_holiday.confirmation.modal.prompt',
          {
            name: flow.name,
          },
        ),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._removeHolidayFlow(flow),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  onAddHolidayCallFlow(): void {
    const newFlow = {
      id: crypto.randomUUID(),
      name: this._translateService.instant(
        'tkey;admin.teams_voice_management.teams_auto_attendant.new_holiday_call_flow.default_name',
      ),
      isHoliday: true,
      holiday: {
        id: crypto.randomUUID(),
        name: '',
        dateTimeRanges: [],
      },
      schedule: null,
      callRoutingOption: CallRoutingOption.DISCONNECT, // This needs to be updated when we modify Destinations,
      selectedGreetingOption: CallFlowGreetingsOptions.NONE,
    } as UiCallFlow;

    this._editContext.addHolidayCallFlow(newFlow);
    this.selectedCallFlow.set(newFlow);
    this._validatedFlowIds.add(newFlow.id);
    setTimeout(() => this._forceValidateAllFlows());
  }

  onHolidayCallFlowNameChange(flowId: string, newName: string): void {
    this._editContext.updateHolidayCallFlowName(flowId, newName);
    if (this.selectedCallFlow()?.id === flowId) {
      this.selectedCallFlow.update((cf) => ({ ...cf, name: newName }));
    }
  }

  onGreetingUpdate(flowId: string, greeting: CallFlowGreetingsEntity): void {
    this._editContext.updateGreeting(
      flowId,
      greeting.greeting,
      greeting.selectedGreetingOption,
      greeting?.isDestinationsGreeting,
      greeting.greetingFile,
    );
    this.greetingUpdate.emit({ flowId, greeting });
  }

  onNavigateToBusinessHours(): void {
    const businessHoursFlow = this.standardCallFlows().find((f) => !f.isAfterHoursCallFlow && !f.isHoliday);
    if (businessHoursFlow) {
      this.onCallFlowSelected(businessHoursFlow);
    }
  }

  areCallFlowComponentsValid(): boolean {
    let allValid = true;
    const selected = this.selectedCallFlow();

    this.callFlowEditComponents.forEach((component) => {
      const flowId = component.callFlow().id;
      this._validatedFlowIds.add(flowId);
      this._editComponentValid.set(flowId, component.validateAndShowErrors());
    });

    if (selected) {
      this.greetingsForm?.triggerSubmitValidation();
      this._greetingsValidByFlowId.set(selected.id, this.greetingsForm?.isFormValid() ?? true);
    }

    if (selected?.isHoliday) {
      this.holidayForm?.triggerSubmitValidation();
      this._holidayValid = this.holidayForm?.isFormValid() ?? true;
    }

    this.callFlowEditComponents.forEach((component) => {
      const flow = component.callFlow();
      const valid = this._isCallFlowValid(flow, selected?.id);
      this._refreshFlowError(flow.id, valid);
      if (!valid) {
        allValid = false;
      }
    });

    return allValid;
  }

  private _isCallFlowValid(flow: UiCallFlow, selectedFlowId: string | undefined): boolean {
    const editValid = this._editComponentValid.get(flow.id) ?? true;
    const greetingsValid = this._resolveGreetingsValid(flow);
    if (!flow.isHoliday) {
      return editValid && greetingsValid;
    }
    const holidayValid = flow.id === selectedFlowId ? this._holidayValid : this._isHolidayFlowDataValid(flow);
    return editValid && greetingsValid && holidayValid;
  }

  private _resolveGreetingsValid(flow: UiCallFlow): boolean {
    const trackedValidity = this._greetingsValidByFlowId.get(flow.id);
    return trackedValidity === undefined ? this._isGreetingsFlowDataValid(flow) : trackedValidity;
  }

  private _isGreetingsFlowDataValid(flow: UiCallFlow): boolean {
    const resolvedOption =
      flow.selectedGreetingOption ??
      (flow.greetingOption?.audio
        ? CallFlowGreetingsOptions.CUSTOM_GREETING
        : flow.greetingOption?.ttsMessage
          ? CallFlowGreetingsOptions.TTS_MESSAGE
          : CallFlowGreetingsOptions.NONE);

    if (resolvedOption === CallFlowGreetingsOptions.CUSTOM_GREETING) {
      return !!flow.greetingOption?.audio || !!flow.greetingFile;
    }
    if (resolvedOption === CallFlowGreetingsOptions.TTS_MESSAGE) {
      return !!flow.greetingOption?.ttsMessage;
    }
    return true;
  }

  private _isHolidayFlowDataValid(flow: UiCallFlow): boolean {
    const trimmedName = flow.name?.trim();
    if (!trimmedName || trimmedName.length > 64) {
      return false;
    }
    const otherHolidays = this.localHolidayCallFlows().filter((other) => other.id !== flow.id);
    const hasDuplicateName = otherHolidays.some(
      (other) => other.name?.trim().toLowerCase() === trimmedName.toLowerCase(),
    );
    if (hasDuplicateName) {
      return false;
    }
    const holidayId = flow.holiday?.id;
    if (!holidayId) {
      return false;
    }
    if (otherHolidays.some((other) => other.holiday?.id === holidayId)) {
      return false;
    }
    return !this._editContext.findOverlappingHolidayCallFlow(flow.id, flow.holiday?.dateTimeRanges ?? []);
  }

  private _revalidateSelectedHolidayForm(): void {
    if (this.selectedCallFlow()?.isHoliday) {
      this.holidayForm?.validateAllFields();
    }
  }

  private _forceValidateAllFlows(): void {
    this.callFlowEditComponents?.forEach((component) => {
      const flowId = component.callFlow().id;
      this._validatedFlowIds.add(flowId);
      component.validateAndShowErrors();
    });
    setTimeout(() => {
      const selected = this.selectedCallFlow();
      if (selected) {
        this.greetingsForm?.triggerSubmitValidation();
        this._greetingsValidByFlowId.set(selected.id, this.greetingsForm?.isFormValid() ?? true);
      }
      if (selected?.isHoliday) {
        this.holidayForm?.triggerSubmitValidation();
        this._holidayValid = this.holidayForm?.isFormValid() ?? true;
      }
      this.callFlowEditComponents?.forEach((component) => {
        const flow = component.callFlow();
        this._editComponentValid.set(flow.id, component.isValid());
        this._refreshFlowError(flow.id, this._isCallFlowValid(flow, selected?.id));
      });
    });
  }

  private _refreshFlowError(flowId: string, isValid: boolean): void {
    this.flowsWithErrors.update((prev) => {
      const next = new Set(prev);
      if (isValid) {
        next.delete(flowId);
      } else {
        next.add(flowId);
      }
      return next;
    });
  }

  private _removeHolidayFlow(flow: UiCallFlow): Observable<void> {
    this._editContext.removeHolidayCallFlow(flow.id);
    this._validatedFlowIds.delete(flow.id);
    this._editComponentValid.delete(flow.id);
    this._greetingsValidByFlowId.delete(flow.id);
    this.flowsWithErrors.update((prev) => {
      const next = new Set(prev);
      next.delete(flow.id);
      return next;
    });

    if (this.selectedCallFlow()?.id === flow.id) {
      const remaining =
        this.localHolidayCallFlows()[this.localHolidayCallFlows().length - 1] ?? this.standardCallFlows()[0] ?? null;
      this.selectedCallFlow.set(remaining);
    }

    return of(null);
  }

  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonStyles = ButtonStyles;
  protected readonly ButtonSizes = ButtonSizes;
}
